import React, { useState } from "react";

import { Box, Button, CircularProgress, Divider, Grid, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { Add, Edit, MoreVert } from "@mui/icons-material";

import { DataLayout, FieldControl, StatusChip } from "../../../shared/components";
import { ExpandableText } from "../../../shared/components/ExpandableText";
import { useDisplayField } from "../../../hooks/useDisplayField";
import { useCapabilities } from "../../../hooks/useCapabilities";

interface RaiseCauseProps {
    causesData: any;
    isCauseFetching: boolean;
    isCausePending: boolean;
    setCausesData: (data: any) => void;
    onClearCause: () => void;
    isSaving: boolean;
    onSaveCause: () => void;
    editCauseStatus: any;
    causeData: any;
    onEditCause: (cause: any) => void;
}

export const RaiseCause: React.FC<RaiseCauseProps> = ({ causesData, isCauseFetching, isCausePending, setCausesData, onClearCause, isSaving, onSaveCause, editCauseStatus, causeData, onEditCause }) => {

    const { isAllowedAddCause, isAllowedEditCause } = useCapabilities();

    const [formStatus, setFormStatus] = useState<boolean>(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const { getFilterOptions } = useDisplayField([]);

    const handleFieldChange = (key: string, value: any) => {
        setCausesData({ ...causesData, [key]: value });
        setErrors((prev) => ({ ...prev, [key]: "" }));
    };

    const validate = () => {
        const newErrors: { [key: string]: string } = {};
        if (!causesData.causeTitle) newErrors.causeTitle = "Title is required";
        if (!causesData.causeStatus) newErrors.causeStatus = "Status is required";
        if (!causesData.causeDescription) newErrors.causeDescription = "Description is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selected, setSelected] = useState<any>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, cause: any) => {
        event.stopPropagation();
        setSelected(cause);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    if (isCauseFetching || isCausePending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box display={'flex'} flexDirection={'column'} gap={2} sx={{ pr: 1 }}>
            {
                !formStatus && isAllowedAddCause && (
                    <Button onClick={() => { setErrors({}); setFormStatus(true); }} variant="outlined">Add Cause</Button>
                )
            }
            {
                formStatus && (
                    <DataLayout title={"Add Cause"} bgVariant="filled">
                        <Grid container spacing={2} p={2}>
                            <Grid size={6}>
                                <FieldControl
                                    disabled={isCauseFetching || isCausePending}
                                    keyName={"causeTitle"}
                                    label={"Title"}
                                    variant={"input"}
                                    value={causesData.causeTitle}
                                    onChange={handleFieldChange}
                                    error={!!errors.causeTitle}
                                    helperText={errors.causeTitle}
                                    required
                                />
                            </Grid>
                            <Grid size={6}>
                                <FieldControl
                                    disabled={isCauseFetching || isCausePending}
                                    keyName={"causeStatus"}
                                    label={"Status"}
                                    variant={"select"}
                                    options={getFilterOptions('RISK_STATUS').map((value) => {
                                        return {
                                            label: value.display_text,
                                            value: value.key
                                        }
                                    })}
                                    value={causesData.causeStatus}
                                    onChange={handleFieldChange}
                                    error={!!errors.causeStatus}
                                    helperText={errors.causeStatus}
                                    required
                                />
                            </Grid>
                            <Grid size={12}>
                                <FieldControl
                                    disabled={isCauseFetching || isCausePending}
                                    keyName={"causeDescription"}
                                    label={"Description"}
                                    variant={"input"}
                                    multiline
                                    value={causesData.causeDescription}
                                    onChange={handleFieldChange}
                                    error={!!errors.causeDescription}
                                    helperText={errors.causeDescription}
                                    required
                                />
                            </Grid>
                            <Grid size={12}>
                                <Divider />
                            </Grid>
                            <Grid size={12} display={'flex'} justifyContent={'flex-end'} gap={2}>
                                <Button
                                    sx={{ color: 'text.primary' }}
                                    onClick={() => {
                                        onClearCause();
                                        setErrors({});
                                        setFormStatus(false);
                                    }}
                                    disabled={isSaving}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    startIcon={<Add />}
                                    onClick={() => {
                                        if (validate()) {
                                            onSaveCause();
                                        }
                                    }}
                                    loading={isSaving}
                                    disabled={isSaving}
                                >
                                    {editCauseStatus.editCauseStatus ? 'Edit Cause' : 'Add Cause'}
                                </Button>
                            </Grid>
                        </Grid>
                    </DataLayout>
                )
            }
            <Box display={'flex'} flexDirection={'column'} gap={2}>
                {
                    causeData?.length === 0 ?
                        <Typography variant="body2" color="text.secondary" textAlign="center" mt={5}>No causes found.</Typography> :
                        causeData.map((cause: any) => (
                            <Box display={'flex'} gap={1} key={cause.id} border={1} borderColor={'divider'} justifyContent={'space-between'} alignItems={'flex-start'} borderRadius={1} padding={2}>
                                <Box display={'flex'} flexDirection={'column'} alignItems={'start'} gap={.5} flex={1}>
                                    <ExpandableText 
                                        text={cause.title || 'N/A'} 
                                        variant="subtitle2" 
                                        color="text.primary"
                                        maxLines={1} 
                                        maxWidth="100%" 
                                    />
                                    <ExpandableText 
                                        text={cause.description || 'N/A'} 
                                        variant="caption" 
                                        color="text.secondary"
                                        maxLines={2} 
                                        maxWidth="100%" 
                                    />
                                </Box>
                                <Box display={'flex'} alignItems={'center'} gap={.5}>
                                    <StatusChip label={cause.status_display_text} color="amber" />
                                    {
                                        isAllowedEditCause && (
                                            <IconButton onClick={(e) => handleMenuOpen(e, cause)}>
                                                <MoreVert fontSize="small" />
                                            </IconButton>
                                        )
                                    }
                                </Box>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleMenuClose}
                                >
                                    <MenuItem
                                        onClick={() => {
                                            onEditCause(selected);
                                            setErrors({});
                                            setFormStatus(true);
                                            handleMenuClose();
                                        }}
                                    >
                                        <Edit fontSize="small" />
                                        <Typography ml={1}>Edit</Typography>
                                    </MenuItem>
                                </Menu>
                            </Box>
                        ))
                }
            </Box>
        </Box>
    )
}